// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import * as StringUtil from 'app/common/lib/StringUtil';
import ActivityItemConfirmModal from 'app/common/react/components/Activities/ActivityItems/ActivityItemConfirmModal';
import ActivityItemFlyout from 'app/common/react/components/Activities/ActivityItems/Flyout';
import Comment from 'app/common/react/components/Comments/Comment';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import reportCommentAction from 'app/common/react/components/undiscovery/FlagComment/action/reportComment';
import type { AggregatedComment } from 'app/packages/pinterest-flow-types/comment';
import type { State as ReduxState } from 'app/common/redux/types';
import { AggregatedCommentLikedByList } from 'app/common/react/components/do/aggregated-pins/did-it/common/LikedByList';
import { pinUpdateCommentCount } from 'app/common/redux/actions/pin/comment';
import { type CloseupViewType } from 'app/common/react/components/closeup/types';
import { updateCommentCount } from 'app/common/redux/actions/aggregatedComments/updateCommentCount';
import { updateUserDidItDataCommentCount } from 'app/common/redux/actions/user_did_it_data/did_it';
import ActivityItemCreate, {
  type ActivityItemEditData,
} from 'app/common/react/components/Activities/ActivityItemCreate/ActivityItemCreate';
import {
  COMMENT_OBJECT_TYPES,
  type CommentObjectType,
} from 'app/common/redux/reducers/aggregatedComments';
import {
  createAggregatedComment,
  type AggregatedCommentCreateData,
} from 'app/common/redux/actions/aggregatedComments/create';
import {
  deleteAggregatedComment,
  type AggregatedCommentDeleteData,
} from 'app/common/redux/actions/aggregatedComments/delete';
import {
  hideAggregatedComment,
  type AggregatedCommentHideData,
} from 'app/common/redux/actions/aggregatedComments/hide';
import {
  MAX_DEPTH,
  ReplyList,
} from 'app/common/react/components/Comments/AggregatedComments/AggregatedCommentList';
import {
  toggleAggregatedCommentLike,
  updateAggregatedComment,
  type AggregatedCommentUpdateData,
  type AggregatedCommentToggleLikeData,
} from 'app/common/redux/actions/aggregatedComments/update';
import { bindActionCreators, type Dispatch } from 'redux';
import { Box, Spinner } from 'gestalt';

type OwnProps = {|
  aggregatedCommentId: string,
  depth: number,
  didItOwnerId?: string,
  isManagedView?: boolean,
  objectType: CommentObjectType,
  parentObjectId: string,
  pinDomainOwnerId?: string,
  variant: CloseupViewType,
  viewerId: string,
|};

type HoCProps = {|
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  createComment: (AggregatedCommentCreateData) => *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  deleteComment: (AggregatedCommentDeleteData) => *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  hideComment: (AggregatedCommentHideData) => *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  toggleLikeComment: (AggregatedCommentToggleLikeData) => *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  updateComment: (AggregatedCommentUpdateData) => *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  updateCommentCount: (id: string, increment: 1 | -1) => *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  updateParentCommentCount: (id: string, increment: 1 | -1) => *,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  reportComment: () => *,
|};

type ReduxProps = {|
  comment: AggregatedComment,
|};

type Props = {| ...OwnProps, ...ReduxProps, ...HoCProps |};

type State = {|
  inEditMode: boolean,
  isLoadingNewCreatedComment: boolean,
  renderActiveModal: () => React.Node,
  showReplyComposer: boolean,
|};

export const REPLY_INDENT = 48;

export class AggregatedCommentContainer extends React.PureComponent<Props, State> {
  state = {
    inEditMode: false,
    isLoadingNewCreatedComment: false,
    renderActiveModal: () => null,
    showReplyComposer: false,
  };

  componentDidMount = () => {
    this.parentObjectTemp.id = this.props.parentObjectId;
  };

  get isReply(): boolean {
    const { depth } = this.props;
    return depth === MAX_DEPTH;
  }

  // TODO(jwardle): Refactor ActivityItemCreate to accept parentObjectId instead of parentObject
  parentObjectTemp = {};

  handleClickLike = () => {
    const {
      aggregatedCommentId,
      comment: { reaction_by_me: isLikedByMe },
      toggleLikeComment,
    } = this.props;
    toggleLikeComment({ aggregatedCommentId, isLikedByMe: !!isLikedByMe });
  };

  toggleReplyComposer = () => this.setState({ showReplyComposer: !this.state.showReplyComposer });

  handleAddComment = (createData: { contentMarkdown: string, tags: string }) => {
    const { aggregatedCommentId, createComment, parentObjectId, updateCommentCount } = this.props;
    let replyToCommentId;
    let objectId = aggregatedCommentId;
    this.toggleReplyComposer();
    this.setState({ isLoadingNewCreatedComment: true });
    if (this.isReply) {
      objectId = parentObjectId;
      replyToCommentId = aggregatedCommentId;
    }
    createComment({
      objectType: COMMENT_OBJECT_TYPES.aggregatedComment,
      objectId,
      tags: createData.tags,
      text: createData.contentMarkdown,
      replyToCommentId,
      // $FlowFixMe Flow v0.104.0 upgrade supression.
    }).then(() => {
      updateCommentCount(this.isReply ? parentObjectId : aggregatedCommentId, 1);
      this.setState({
        isLoadingNewCreatedComment: false,
      });
    });
  };

  handleDelete = () => {
    const {
      aggregatedCommentId,
      deleteComment,
      updateParentCommentCount,
      parentObjectId,
    } = this.props;
    deleteComment({ aggregatedCommentId, parentId: parentObjectId });
    updateParentCommentCount(parentObjectId, -1);
  };

  handleOnEdit = () => {
    this.setState({ inEditMode: true });
  };

  handleOnEdited = (editData: ActivityItemEditData) => {
    const { aggregatedCommentId, updateComment } = this.props;
    this.setState({ inEditMode: false });
    updateComment({
      commentId: aggregatedCommentId,
      tags: editData.tags,
      text: editData.contentMarkdown,
    });
  };

  handleOnEditCancel = () => {
    this.setState({ inEditMode: false });
  };

  updateLikesCount = (count: number, adjuster: boolean): number =>
    adjuster ? count - 1 : count + 1;

  showConfirmDeleteModal = () => {
    this.setState({ renderActiveModal: this.renderConfirmDeleteModal });
  };

  showLikeListModal = () => {
    this.setState({ renderActiveModal: this.renderLikeListModal });
  };

  hideModal = () => {
    this.setState({ renderActiveModal: () => null });
  };

  renderFlyout = () => {
    const {
      comment: {
        user: { id: commentAuthorId },
      },
      didItOwnerId,
      pinDomainOwnerId,
      reportComment,
      viewerId,
    } = this.props;

    const showDelete =
      viewerId === commentAuthorId || viewerId === didItOwnerId || viewerId === pinDomainOwnerId;
    const showEdit = viewerId === commentAuthorId;
    const showReport = viewerId !== commentAuthorId;

    return (
      <ActivityItemFlyout
        handleClickReport={showReport ? reportComment : null}
        handleDelete={showDelete ? this.showConfirmDeleteModal : null}
        handleOnEdit={showEdit ? this.handleOnEdit : null}
        small
      />
    );
  };

  // TODO(jwardle): Move modal render functions to their own files, shared with activityItem modals
  renderConfirmDeleteModal = () => (
    <ActivityItemConfirmModal
      accessibilityModalLabel={i18n._('Deleting a comment', 'Label for deleting a comment')}
      heading={i18n._('Are you sure?', 'Are you sure to remove post or comment')}
      cancelButtonText={i18n._('Cancel', 'Cancel deleting comment')}
      confirmButtonText={i18n._('Delete', 'Delete comment')}
      detail={i18n._('This comment will be deleted', 'confirmation before deleting comment')}
      onConfirm={this.handleDelete}
      onDismiss={this.hideModal}
    />
  );

  renderLikeListModal = () => {
    const {
      aggregatedCommentId,
      comment: { reaction_counts: reactionCounts },
    } = this.props;
    const likeCount = reactionCounts[PCONST.thrift.ReactionType.LIKE];

    return (
      <Modal
        accessibilityCloseLabel={i18n._('close', 'accessible label for button to close modal')}
        accessibilityModalLabel={i18n._(
          'Users who have liked this.',
          'Label for a modal showing a list of users who have liked a Tried Item Comment',
        )}
        heading={StringUtil.safeFormat(
          i18n.ngettext('{{count}} Like', '{{count}} Likes', likeCount),
          { count: likeCount.toString() },
        )}
        onDismiss={this.hideModal}
        size="md"
      >
        <AggregatedCommentLikedByList id={aggregatedCommentId} />,
      </Modal>
    );
  };

  render() {
    if (!this.props.comment) {
      return null;
    }
    const {
      aggregatedCommentId,
      comment: {
        comment_count: commentCount,
        created_at: createdAt,
        reaction_by_me: isLikedByMe,
        reaction_counts: reactionCounts,
        tags,
        tagged_users,
        text,
        user: { full_name: fullName },
      },
      comment,
      depth,
      didItOwnerId,
      isManagedView,
      parentObjectId,
      pinDomainOwnerId,
      variant,
    } = this.props;

    const {
      inEditMode,
      isLoadingNewCreatedComment,
      renderActiveModal,
      showReplyComposer,
    } = this.state;
    const showReplies = commentCount > 0;
    const composerIndent = this.isReply ? 0 : REPLY_INDENT;

    return (
      <React.Fragment>
        <Comment
          avatarSize={this.isReply ? 'sm' : 'md'}
          commentAuthor={comment.user}
          commentCount={commentCount}
          commentId={aggregatedCommentId}
          contentText={text}
          createdAt={createdAt}
          flyout={this.renderFlyout()}
          handleLikeClick={this.handleClickLike}
          handleLikeCountClick={this.showLikeListModal}
          handleOnEditCancel={this.handleOnEditCancel}
          handleOnEdited={this.handleOnEdited}
          handleReplyClick={this.toggleReplyComposer}
          hideAddObjects
          hideReplyCount
          inEditMode={inEditMode}
          isLikedByMe={!!isLikedByMe}
          parentObject={this.parentObjectTemp}
          reactionCounts={reactionCounts}
          taggedUsers={tagged_users}
          tags={tags}
          truncateText
          type={COMMENT_OBJECT_TYPES.aggregatedComment}
          variant={variant}
        />
        {showReplyComposer && (
          <Box
            dangerouslySetInlineStyle={{ __style: { paddingLeft: composerIndent } }}
            data-test-id="reply-composer"
          >
            <Box display="flex" marginTop={2}>
              <Box flex="grow">
                <ActivityItemCreate
                  avatarSize="sm"
                  hideAddObjects
                  onAddItem={this.handleAddComment}
                  onCancelItem={this.toggleReplyComposer}
                  onEnter={() => {}}
                  objectType={COMMENT_OBJECT_TYPES.aggregatedComment}
                  parentObjectId={this.parentObjectTemp.id}
                  replyToObject={this.isReply ? comment : null}
                  statusType={'textInflightCreation'}
                />
              </Box>
            </Box>
          </Box>
        )}
        {showReplies && (
          <Box dangerouslySetInlineStyle={{ __style: { paddingLeft: REPLY_INDENT } }}>
            <ReplyList
              comment={comment}
              commentCount={commentCount}
              didItOwnerId={didItOwnerId}
              depth={depth}
              isManagedView={isManagedView}
              objectId={aggregatedCommentId}
              objectType={COMMENT_OBJECT_TYPES.aggregatedComment}
              parentObjectId={parentObjectId}
              pinDomainOwnerId={pinDomainOwnerId}
              replyToName={fullName}
              variant="legacy"
            />
            <Spinner
              accessibilityLabel={i18n._('Comments loading')}
              show={isLoadingNewCreatedComment}
            />
          </Box>
        )}
        {renderActiveModal()}
      </React.Fragment>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: OwnProps) => {
  const { aggregatedCommentId, objectType } = ownProps;
  const updateParentCommentCountMap = {
    [COMMENT_OBJECT_TYPES.aggregatedComment]: bindActionCreators(updateCommentCount, dispatch),
    [COMMENT_OBJECT_TYPES.didIt]: bindActionCreators(updateUserDidItDataCommentCount, dispatch),
    [COMMENT_OBJECT_TYPES.pin]: bindActionCreators(pinUpdateCommentCount, dispatch),
  };

  return {
    createComment: bindActionCreators(createAggregatedComment, dispatch),
    deleteComment: bindActionCreators(deleteAggregatedComment, dispatch),
    toggleLikeComment: bindActionCreators(toggleAggregatedCommentLike, dispatch),
    updateComment: bindActionCreators(updateAggregatedComment, dispatch),
    hideComment: bindActionCreators(hideAggregatedComment, dispatch),
    updateCommentCount: bindActionCreators(updateCommentCount, dispatch),
    updateParentCommentCount: updateParentCommentCountMap[objectType],
    reportComment: () =>
      dispatch(
        reportCommentAction({
          id: aggregatedCommentId,
          viewType: null,
          viewParameter: null,
        }),
      ),
  };
};

export default connect<_, OwnProps, _, _, _, _>(
  (state: ReduxState, props: OwnProps): ReduxProps => {
    const comment = state.aggregatedComments[props.aggregatedCommentId];
    return {
      comment,
    };
  },
  mapDispatchToProps,
)(AggregatedCommentContainer);
